import 'bootstrap/dist/css/bootstrap.css';
import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import axios from 'axios';
import App from './App';
import './css/design-tokens.css';
import './css/print.css';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';

const MEETING_SCOPED_PAGES = new Set([
  'live-timetable', 'draw', 'draw-export-pdf', 'racecards', 'race-cards',
  'competition-admin', 'entry-admin', 'events-admin', 'banding-helper', 'victor-ludorum',
  'system-settings', 'registration-desk', 'racing-dashboard',
  'commentary', 'club-summary'
]);

function getMeetingIdFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const rawMeetingId = String(params.get('meetingId') || '').trim();
  const parsed = Number.parseInt(rawMeetingId, 10);
  return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : '';
}

function getSlugFromPathname() {
  const segments = String(window.location.pathname || '')
    .split('/')
    .filter(Boolean);
  if (segments.length < 2) return '';

  const [slug, page] = segments;
  if (!MEETING_SCOPED_PAGES.has(page)) return '';
  return String(slug || '').trim().toLowerCase();
}

function getMeetingSlugFromUrl() {
  return getSlugFromPathname();
}

const slugMeetingIdCache = new Map();
const slugResolutionInFlight = new Map();

async function resolveMeetingIdForCurrentUrl(originalFetch) {
  const directMeetingId = getMeetingIdFromUrl();
  if (directMeetingId) return directMeetingId;

  const slug = getSlugFromPathname();
  if (!slug) return '';

  if (slugMeetingIdCache.has(slug)) {
    return slugMeetingIdCache.get(slug);
  }

  if (slugResolutionInFlight.has(slug)) {
    return slugResolutionInFlight.get(slug);
  }

  const pending = originalFetch(`/api/public/meeting-by-slug/${encodeURIComponent(slug)}`)
    .then(async (res) => {
      if (!res.ok) return '';
      const data = await res.json().catch(() => ({}));
      const parsed = Number.parseInt(String(data?.meetingId || '').trim(), 10);
      const meetingId = Number.isFinite(parsed) && parsed > 0 ? String(parsed) : '';
      if (meetingId) {
        slugMeetingIdCache.set(slug, meetingId);
      }
      return meetingId;
    })
    .catch(() => '')
    .finally(() => {
      slugResolutionInFlight.delete(slug);
    });

  slugResolutionInFlight.set(slug, pending);
  return pending;
}

const originalFetch = window.fetch.bind(window);
window.fetch = async (input, init) => {
  const requestUrl = typeof input === 'string' ? input : String(input?.url || '');
  if (requestUrl.includes('/api/public/meeting-by-slug/')) {
    return originalFetch(input, init);
  }

  const meetingId = await resolveMeetingIdForCurrentUrl(originalFetch);
  const baseHeaders = init?.headers || (input instanceof Request ? input.headers : undefined);
  const headers = new Headers(baseHeaders || {});
  const meetingSlug = getMeetingSlugFromUrl();
  if (meetingSlug && !headers.has('x-meeting-slug')) {
    headers.set('x-meeting-slug', meetingSlug);
  }

  if (!meetingId) {
    return originalFetch(input, {
      ...(init || {}),
      headers
    });
  }

  if (!headers.has('x-meeting-id')) {
    headers.set('x-meeting-id', meetingId);
  }

  return originalFetch(input, {
    ...(init || {}),
    headers
  });
};

axios.interceptors.request.use(async (config) => {
  const configUrl = String(config?.url || '');
  if (configUrl.includes('/api/public/meeting-by-slug/')) {
    return config;
  }

  const meetingId = await resolveMeetingIdForCurrentUrl(originalFetch);
  const headers = { ...(config.headers || {}) };
  const meetingSlug = getMeetingSlugFromUrl();
  if (meetingSlug && !headers['x-meeting-slug']) {
    headers['x-meeting-slug'] = meetingSlug;
  }

  if (!meetingId) {
    return {
      ...config,
      headers
    };
  }

  if (!headers['x-meeting-id']) {
    headers['x-meeting-id'] = meetingId;
  }

  return {
    ...config,
    headers
  };
});

const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href');
const rootElement = document.getElementById('root');

ReactDOM.render(
  <BrowserRouter basename={baseUrl}>
    <App />
  </BrowserRouter>,
  rootElement);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
